<script setup lang="ts">
const { t } = useTexts();
const route = useRoute('user-validate-type-token');
const { getSecureQueryParam } = useRoutes();
const { ApiService } = useApiService();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { fetchUserProfile } = useUserData();
const { isEmailVerified, isPhoneVerifiedOrComplete } = useUserValidations();
const { openToaster } = useLayoutStore();

const isLoading = ref(true);
const isSuccess = ref(false);
const isError = ref(false);
const isRedirecting = ref(false);

const validateType = computed(() => {
  return getSecureQueryParam(route?.params?.type);
});

const validateHash = computed(() => {
  return getSecureQueryParam(route?.params?.token);
});

const validateTypeText = computed(() => {
  return validateType.value === 'email'
    ? t('account.validate.email')
    : t('account.validate.phone');
});

const isValidateTypeValid = computed(() => {
  return validateType.value === 'email' || validateType.value === 'phone';
});

const isEmailAlreadyVerified = computed(() => {
  return validateType.value === 'email' && isEmailVerified.value;
});

const isPhoneAlreadyVerified = computed(() => {
  return validateType.value === 'phone' && isPhoneVerifiedOrComplete.value;
});

useSeo({
  title: t('account.validate.title'),
});

const progressTotalInMs = 6000;
const stepsToInterval = progressTotalInMs / 100;
const progress = ref(0);

function startProgress() {
  const interval = setInterval(() => {
    progress.value += 1;
  }, stepsToInterval);

  setTimeout(() => {
    clearInterval(interval);
    progress.value = 100;
  }, progressTotalInMs);
}

async function validateUserCode() {
  if (
    !validateType.value ||
    !validateHash.value ||
    !isValidateTypeValid.value
  ) {
    openToaster({
      message: t('account.validate.error'),
      type: 'error',
    });

    return;
  }

  if (isEmailAlreadyVerified.value) {
    openToaster({
      message: t('account.validate.error_already_verified', {
        type: validateTypeText.value,
      }),
      type: 'error',
    });

    await sleep(500);

    isRedirecting.value = true;

    await sleep(3000);

    await navigateTo(localeLink('user-account'));

    return;
  }

  if (isPhoneAlreadyVerified.value) {
    openToaster({
      message: t('account.validate.error_already_verified', {
        type: validateTypeText.value,
      }),
      type: 'error',
    });

    await sleep(500);

    isRedirecting.value = true;

    await sleep(3000);

    await navigateTo(localeLink('user-account'));

    return;
  }

  startProgress();

  const isProgressFinished = ref(false);

  setTimeout(() => {
    isProgressFinished.value = true;
  }, progressTotalInMs);

  await ApiService.post(`bff/validate-confirmation`, {
    type: validateType.value,
    hash: validateHash.value,
  })
    .then(async (res) => {
      await new Promise((resolve) => {
        setInterval(() => {
          if (isProgressFinished.value) {
            resolve(true);
          }
        }, stepsToInterval);
      });

      isLoading.value = false;
      isSuccess.value = true;

      const message = res.data.message
        ? res.data.message
        : t('account.validate.success', {
            type: validateTypeText.value,
          });

      openToaster({
        message,
        type: 'success',
      });

      await fetchUserProfile({ force: true });

      isRedirecting.value = true;

      await sleep(2000);

      await navigateTo(localeLink('user-account'));
    })
    .catch(async (err) => {
      await new Promise((resolve) => {
        setInterval(() => {
          if (isProgressFinished.value) {
            resolve(true);
          }
        }, stepsToInterval);
      });

      isLoading.value = false;
      isError.value = true;

      const message = err.data.message
        ? err.data.message
        : t('account.validate.error_validate', {
            type: validateTypeText.value,
          });

      openToaster({
        message,
        type: 'error',
      });
    });
}

watch(
  isAuthenticated,
  async () => {
    if (!isAuthenticated.value) {
      openLogin();
    } else {
      await sleep(1000);
      await validateUserCode();
    }
  },
  { immediate: true },
);
</script>

<template>
  <UserAccountPageLayout
    on-back="user-account"
    :show-user-menu="isAuthenticated"
    :title="$t('account.validate.title')"
  >
    <UserBox>
      <div :class="$style.validatePage">
        <div :class="$style.icon">
          <img
            v-if="!isValidateTypeValid"
            alt="Error icon"
            aria-hidden="true"
            :src="getAssetsUrlForCDN('/assets/images/svg/loading.svg')"
          />
          <img
            v-else-if="validateType === 'email'"
            alt="Email icon"
            aria-hidden="true"
            :src="getAssetsUrlForCDN('/assets/images/svg/email.svg')"
          />
          <img
            v-else
            alt="Sms icon"
            aria-hidden="true"
            :src="getAssetsUrlForCDN('/assets/images/svg/sms.svg')"
          />
        </div>

        <div
          v-if="!isValidateTypeValid"
          :class="$style.text"
        >
          <div :class="[$style.title, $style.error]">
            <NuxtIcon name="close" />
            <span>
              {{ $t('account.validate.error') }}
            </span>
          </div>

          <NuxtLink
            :class="$style.button"
            :to="localeLink('user-account')"
          >
            {{ $t('account.validate.back_to_account') }}
          </NuxtLink>
        </div>

        <div
          v-else-if="!isAuthenticated"
          :class="$style.text"
        >
          <div :class="$style.title">
            <NuxtIcon name="user/user_circle" />
            <span>
              {{
                $t('account.validate.error_not_authenticated', {
                  type: validateTypeText,
                })
              }}
            </span>
          </div>

          <button
            :class="[$style.button, $style.login]"
            @click="openLogin"
          >
            {{ $t('account.validate.login') }}
          </button>
        </div>

        <div
          v-else-if="isEmailAlreadyVerified || isPhoneAlreadyVerified"
          :class="$style.text"
        >
          <div :class="[$style.title, $style.success]">
            <NuxtIcon name="check" />
            <span>
              {{
                $t('account.validate.error_already_verified', {
                  type: validateTypeText,
                })
              }}
            </span>
          </div>

          <NuxtLink
            v-if="isRedirecting"
            :class="$style.button"
            :to="localeLink('user-account')"
          >
            <NuxtIcon
              :class="$style.loadingIcon"
              filled
              name="loading"
            />
            {{ $t('account.validate.redirecting') }}
          </NuxtLink>

          <NuxtLink
            v-else
            :class="$style.button"
            :to="localeLink('user-account')"
          >
            {{ $t('account.validate.back_to_account') }}
          </NuxtLink>
        </div>

        <div
          v-else
          :class="$style.text"
        >
          <div
            v-if="isSuccess"
            :class="[$style.title, $style.success]"
          >
            <NuxtIcon name="check" />
            <span>
              {{
                $t('account.validate.success', {
                  type: validateTypeText,
                })
              }}
            </span>
          </div>

          <div
            v-else-if="isError"
            :class="[$style.title, $style.error]"
          >
            <NuxtIcon name="close" />
            <span>
              {{
                $t('account.validate.error_validate', {
                  type: validateTypeText,
                })
              }}
            </span>
          </div>

          <div
            v-else
            :class="[$style.title, $style.loading]"
          >
            <NuxtIcon
              :class="$style.loadingIcon"
              filled
              name="loading"
            />
            <span>
              {{
                $t('account.validate.loading', {
                  type: validateTypeText,
                })
              }}
            </span>
          </div>

          <div :class="$style.description">
            {{ $t('account.validate.token') }}
            {{ validateHash }}
          </div>

          <div :class="$style.progress">
            <div :class="$style.progressBar">
              <div
                :class="[
                  $style.progressBarFill,
                  { '!bg-success': isSuccess },
                  { '!bg-error': isError },
                ]"
                :style="{ width: `${progress}%` }"
              >
                <div
                  v-if="isLoading"
                  :class="$style.progressBarEffect"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserBox>
  </UserAccountPageLayout>
</template>

<style module lang="scss">
.validatePage {
  @apply flex flex-col md:flex-row items-center justify-center gap-5 py-4 px-1 md:py-6 md:px-6;

  .icon {
    @apply select-none;

    img {
      @apply min-w-16 w-16 h-16 lg:min-w-20 lg:w-20 lg:h-20 mx-auto;
    }
  }

  .text {
    @apply flex flex-col justify-center items-center md:items-start gap-2;

    .loadingIcon {
      @apply animate-spin;
    }

    .title {
      @apply flex items-center gap-2 font-bold text-xs md:text-sm lg:text-base leading-3 lg:-mt-2;

      &.success {
        @apply text-success;
      }

      &.error {
        @apply text-error;
      }

      &.loading {
        @apply text-texts;
      }
    }

    .description {
      @apply text-xs lg:text-sm text-texts/60 font-medium max-w-[80vw] overflow-hidden overflow-ellipsis whitespace-nowrap leading-3;
    }

    .progress {
      @apply w-full min-w-full md:min-w-[420px] h-2 bg-bg-primary rounded-full mt-px;

      .progressBar {
        @apply h-full bg-texts/20 rounded-full relative;

        .progressBarFill {
          @apply h-full bg-primary rounded-full;

          .progressBarEffect {
            @apply absolute inset-0 top-0 left-0 w-full h-2 rounded-full;

            background-image: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255, 255, 255, 0.2) 10px,
              rgba(255, 255, 255, 0.2) 20px
            );

            animation: slide 600ms linear infinite;
            will-change: background-position;
          }
        }
      }
    }

    .button {
      @apply text-xs bg-texts/20 text-texts/80 font-medium px-3 py-1.5 rounded-full flex items-center justify-center gap-2 mt-1;

      &.login {
        @apply bg-button-bg text-button-text font-semibold;
      }
    }
  }
}

@keyframes slide {
  from {
    background-position-x: 0;
  }
  to {
    background-position-x: 28.28px; // valor simplificado
  }
}
</style>
